/**
 * Officer phone-call reminders for the current session: the live blocks that
 * need a call (from the roster), minus the ones the shared log says someone
 * has already phoned about.
 *
 * The blocks recompute on every roster change, so a block that is added or
 * edited re-arms instantly. The log is re-fetched whenever another user marks
 * or re-arms something (`liveUpdateTick`, bumped by the call_ack_update
 * websocket message).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '@/api/client';
import type { CallAck, OfficerMetadata } from '@/types/api';
import {
  ackKey, callAckKeys, computeCallBlocks, officerCallStates,
  type CallBlock, type OfficerCallState,
} from '@/utils/callBlocks';

export function useCallAcks(
  counterMatrix: string[][][] | undefined,
  officerMetadata: Record<string, OfficerMetadata> | undefined,
  initialSchedules: Record<string, number[]> | null | undefined,
  sessionId: string | undefined,
  liveUpdateTick: number,
) {
  const [acks, setAcks] = useState<CallAck[]>([]);

  useEffect(() => {
    if (!sessionId || !counterMatrix) return;
    let cancelled = false;
    apiClient.callAcks.list()
      .then((list) => { if (!cancelled) setAcks(list); })
      .catch(() => { /* keep the last known log */ });
    return () => { cancelled = true; };
    // counterMatrix only gates the first fetch — the log doesn't change with roster edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, liveUpdateTick, !!counterMatrix]);

  const blocks = useMemo<CallBlock[]>(
    () => (counterMatrix && officerMetadata ? computeCallBlocks(counterMatrix, officerMetadata, initialSchedules) : []),
    [counterMatrix, officerMetadata, initialSchedules],
  );
  const acked = useMemo(() => callAckKeys(acks), [acks]);
  const byOfficer = useMemo<Map<string, OfficerCallState>>(() => officerCallStates(blocks, acked), [blocks, acked]);

  /** Mark blocks as phoned about (`called`), or re-arm them. Optimistic; the server's log wins. */
  const setCalled = useCallback(async (officerId: string, signatures: string[], called: boolean) => {
    const sigs = new Set(signatures);
    setAcks((prev) => called
      ? [...prev, ...signatures.filter((s) => !prev.some((a) => ackKey(a.officer_id, a.signature) === ackKey(officerId, s)))
          .map((signature) => ({ officer_id: officerId, signature }))]
      : prev.filter((a) => !(a.officer_id === officerId && sigs.has(a.signature))));
    try {
      setAcks(await apiClient.callAcks.set(officerId, signatures, called));
    } catch (e) {
      setAcks(await apiClient.callAcks.list().catch(() => []));
      throw e;
    }
  }, []);

  return { blocks, acked, byOfficer, setCalled };
}
